// SPDX-License-Identifier: AGPL-3.0-or-later
// SPDX-FileCopyrightText: 2026 Dobrev IT Ltd

import { useEffect, useState } from "react";
import { App, Button, Space, Tag, Typography } from "antd";
import { bioLockAvailable, disableBioLock, enableBioLock, isBioLocked } from "../biolock";

// BioLockPanel is the NATIVE-only at-rest tier: lock the device keys behind this phone's fingerprint
// / face OR its PIN/pattern/password, enforced by the OS via an Android Keystore key (see biolock.ts).
// Keys are already encrypted at rest by default; this adds an OS-gated layer that the Capacitor
// WebView can't get through WebAuthn (no PRF there). Renders nothing where the platform can't offer
// it (the web build, or a phone with no biometric/credential).
export function BioLockPanel({ userId }: { readonly userId: string }) {
  const { message } = App.useApp();
  const [available, setAvailable] = useState<boolean | null>(null);
  const [locked, setLocked] = useState(isBioLocked(userId));
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    void bioLockAvailable().then(setAvailable);
  }, []);

  async function enable() {
    setBusy(true);
    try {
      const n = await enableBioLock(userId); // BiometricPrompt → protect → move slots into the blob
      setLocked(true);
      message.success(`Locked ${n} device key${n === 1 ? "" : "s"} behind your fingerprint / device PIN`);
    } catch (e) {
      message.error(`Couldn't enable: ${(e as Error).message}`);
    } finally {
      setBusy(false);
    }
  }

  async function disable() {
    setBusy(true);
    try {
      await disableBioLock(userId); // BiometricPrompt → unlock → rewrite the (sealed) slots
      setLocked(false);
      message.success("Fingerprint / device-PIN lock removed — keys stay encrypted at rest");
    } catch (e) {
      message.error(`Couldn't remove: ${(e as Error).message}`);
    } finally {
      setBusy(false);
    }
  }

  // Native-only: render nothing until availability resolves / where the device can't offer it, so the
  // section simply doesn't appear in the web client.
  if (available !== true) {
    return null;
  }

  return (
    <Space direction="vertical" style={{ width: "100%" }} size="middle">
      <Typography.Text>
        Status: {locked ? <Tag color="green">locked to this device</Tag> : <Tag>not set</Tag>}
      </Typography.Text>
      <Typography.Paragraph type="secondary" style={{ marginBottom: 0 }}>
        Lock this device's keys behind your <strong>fingerprint / face</strong> or this phone's{" "}
        <strong>PIN / pattern / password</strong>. The encryption key lives in the phone's secure hardware and is
        unusable until the OS verifies you, so even a rooted or stolen phone can't read your keys. You'll confirm once
        each session. If you reset your screen lock the keys here become unreadable — recover via your key backup.
      </Typography.Paragraph>
      {locked ? (
        <Button danger onClick={disable} loading={busy} className="bio-disable">
          Remove fingerprint / device-PIN lock
        </Button>
      ) : (
        <Button type="primary" onClick={enable} loading={busy} className="bio-enable">
          Lock keys with fingerprint / device PIN
        </Button>
      )}
    </Space>
  );
}
